import React, { useState, useEffect } from 'react';
import {
  House,
  Link,
  ChartBar,
  Sparkle,
  Gear,
  PaperPlaneTilt,
  GridFour,
  User,
  Calendar,
  RocketLaunch,
  UploadSimple,
  ArrowsClockwise,
  SpeakerHigh,
  PhoneX,
  CalendarCheck,
  PencilSimple,
  X,
  Plus
} from "@phosphor-icons/react";
import './App.css';
import ClientCRM from './ClientCRM';
import ReactMarkdown from 'react-markdown';
import TalkToKale, { startCall, stopCall, subscribeToCallStatus } from './TalkToKale';
import UserSound from './UserSound';
import ClientProfileCards from './ClientProfileCards';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [userDetails, setUserDetails] = useState(null);
  const [activePage, setActivePage] = useState('home');

  // Check if user is already logged in
  useEffect(() => {
    const savedAuthToken = localStorage.getItem('authToken');
    const savedUserDetails = localStorage.getItem('userDetails');
    
    if (savedAuthToken && savedUserDetails) {
      setAuthToken(savedAuthToken);
      setUserDetails(JSON.parse(savedUserDetails));
      setIsLoggedIn(true);
    }
  }, []);

  const handleLoginSuccess = (token, details) => {
    setAuthToken(token);
    setUserDetails(details);
    setIsLoggedIn(true);
    
    // Save to localStorage for persistence
    localStorage.setItem('authToken', token);
    localStorage.setItem('userDetails', JSON.stringify(details));
  };

  const handleLogout = () => {
    // Clear auth data
    setAuthToken('');
    setUserDetails(null);
    setIsLoggedIn(false);
    
    // Remove from localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('userDetails');
    localStorage.removeItem('chatMessages');
    localStorage.removeItem('threadId');
    
    // Reset to home page
    setActivePage('home');
  };

  const handleNavClick = (page) => {
    setActivePage(page);
  };

  const renderContent = () => {
    switch (activePage) {
      case 'leads_get':
        return <ClientCRM pageType="get" />;
      case 'leads_keep':
        return <ClientCRM pageType="keep" />;
      case 'leads_recover':
        return <ClientCRM pageType="recover" />;
      case 'upload':
        return <UploadPage userDetails={userDetails} onNavClick={handleNavClick} />;
      case 'settings':
        return <SettingsPage userDetails={userDetails} onLogout={handleLogout} />;
      default:
        return <CalendarInterface userDetails={userDetails} />;
    }
  };

  return (
    <div className="app-container">
      {isLoggedIn ? (
        <>
          <SideNavigation 
            userDetails={userDetails} 
            activePage={activePage} 
            onNavClick={handleNavClick}
            onLogout={handleLogout}
          />
          <main className="main-content">
            {renderContent()}
          </main>
        </>
      ) : (
        <LoginScreen onLoginSuccess={handleLoginSuccess} />
      )}
    </div>
  );
}

function LoginScreen({ onLoginSuccess }) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const sendVerificationCode = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Format phone number (remove spaces, ensure it starts with 27)
      let formattedNumber = phoneNumber.replace(/\s+/g, '').replace(/\D/g, '');
      if (!formattedNumber.startsWith('27')) {
        if (formattedNumber.startsWith('0')) {
          formattedNumber = '27' + formattedNumber.substring(1);
        } else {
          formattedNumber = '27' + formattedNumber;
        }
      }
      
      console.log('Sending formatted number:', formattedNumber);
      
      // Convert the string to an integer
      const numberAsInt = parseInt(formattedNumber);
      
      // Create URL with number parameter
      const url = `https://xin7-2on4-t5ue.n7.xano.io/api:kvG3YrJD/auth/log_in?number=${numberAsInt}&code=0`;
      
      console.log('Requesting URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      const data = await response.json();
      console.log('API response:', data);
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to send verification code');
      }
      
      setIsCodeSent(true);
    } catch (error) {
      setError('Error sending code. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Format phone number
      let formattedNumber = phoneNumber.replace(/\s+/g, '').replace(/\D/g, '');
      if (!formattedNumber.startsWith('27')) {
        if (formattedNumber.startsWith('0')) {
          formattedNumber = '27' + formattedNumber.substring(1);
        } else {
          formattedNumber = '27' + formattedNumber;
        }
      }
      
      // Convert the string to an integer
      const numberAsInt = parseInt(formattedNumber);
      const codeAsInt = parseInt(verificationCode);
      
      console.log('Verifying with number:', numberAsInt, 'code:', codeAsInt);
      
      // Create URL with number and code parameters
      const url = `https://xin7-2on4-t5ue.n7.xano.io/api:kvG3YrJD/auth/log_in?number=${numberAsInt}&code=${codeAsInt}`;
      
      console.log('Requesting URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      const data = await response.json();
      console.log('API verification response:', data);
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to verify code');
      }
      
      // Use the auth and user_details as expected from the response format
      // The response format is now expected to be:
      // {"user_details":{...}, "auth":"token"}
      onLoginSuccess(data.auth, data.user_details);
    } catch (error) {
      setError('Invalid code. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isCodeSent) {
      verifyCode();
    } else {
      sendVerificationCode();
    }
  };

  return (
    <div className="login-screen">
      <div className="login-left-panel">
        <div className="login-container">
          <div className="login-logo">
            <img src="/logo.png" alt="Kale" />
            <h1>Kale</h1>
          </div>
          
          <div className="login-welcome">
            <h2>Welcome back 👋</h2>
            <p>Please enter your details to sign in to your agent account</p>
          </div>
          
          <div className="login-form-container">
            <form onSubmit={handleSubmit} className="login-form">
              {!isCodeSent ? (
                <div className="form-group">
                  <label>Mobile Number</label>
                  <div className="phone-input">
                    <span className="country-code">+27</span>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="Enter your phone number"
                      required
                    />
                  </div>
                  <p className="helper-text">We'll send a verification code to your WhatsApp</p>
                </div>
              ) : (
                <div className="form-group">
                  <label>Verification Code</label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="Enter the code from WhatsApp"
                    required
                  />
                  <p className="helper-text">Please enter the 6-digit code we sent you</p>
                </div>
              )}
              
              {error && <div className="error-message">{error}</div>}
              
              <button type="submit" className="login-button" disabled={isLoading}>
                {isLoading ? 'Processing...' : isCodeSent ? 'Verify & Sign In' : 'Send Code'}
              </button>
              
              {isCodeSent && (
                <button 
                  type="button" 
                  className="back-button"
                  onClick={() => setIsCodeSent(false)}
                >
                  Back to Phone Number
                </button>
              )}
            </form>
            
            <div className="login-footer">
              Need help? For fast support: WhatsApp <a href="https://wa.me/27686006875" target="_blank" rel="noopener noreferrer">+27 68 600 6875</a><br/>
              For email support: <a href="mailto:support@kale-app.com">support@kale-app.com</a>
            </div>
          </div>
        </div>
      </div>
      <div className="login-right-panel"></div>
    </div>
  );
}

function SideNavigation({ userDetails, activePage, onNavClick, onLogout }) {
  return (
    <nav className="side-nav">
      <div className="logo-container">
        <img src="/logo.png" alt="Kale" className="logo" />
        <span>Kale</span>
      </div>
      
      <div className="nav-links">
        <div onClick={() => onNavClick('home')}>
          <NavItem 
            label="Home" 
            icon={<House weight="fill" size={20} />} 
            active={activePage === 'home'} 
            selected={activePage === 'home'} 
          />
        </div>
        <div onClick={() => onNavClick('leads_get')}>
          <NavItem 
            label="Get" 
            icon={<RocketLaunch weight="fill" size={20} />} 
            active={activePage === 'leads_get'} 
            selected={activePage === 'leads_get'} 
          />
        </div>
        <div onClick={() => onNavClick('leads_keep')}>
          <NavItem 
            label="Keep" 
            icon={<Link weight="fill" size={20} />} 
            active={activePage === 'leads_keep'} 
            selected={activePage === 'leads_keep'} 
          />
        </div>
        <div onClick={() => onNavClick('leads_recover')}>
          <NavItem 
            label="Recover" 
            icon={<ArrowsClockwise weight="fill" size={20} />} 
            active={activePage === 'leads_recover'} 
            selected={activePage === 'leads_recover'} 
          />
        </div>
        <div onClick={() => onNavClick('upload')}>
          <NavItem 
            label="Upload" 
            icon={<UploadSimple weight="fill" size={20} />} 
            active={activePage === 'upload'} 
            selected={activePage === 'upload'} 
          />
        </div>
        <div onClick={() => onNavClick('settings')}>
          <NavItem 
            label="Settings" 
            icon={<Gear weight="fill" size={20} />} 
            active={activePage === 'settings'} 
            selected={activePage === 'settings'} 
          />
        </div>
      </div>
      
      {userDetails && (
        <div className="user-profile">
          {userDetails.profile_pic ? (
            <img src={userDetails.profile_pic.url} alt={`${userDetails.first_name} ${userDetails.last_name}`} className="profile-image" />
          ) : (
            <div className="profile-initial">{userDetails.first_name?.charAt(0) || 'U'}</div>
          )}
          <div className="user-info">
            <span className="user-name">{userDetails.first_name} {userDetails.last_name}</span>
            <span className="user-role">{userDetails.role_title}</span>
          </div>
        </div>
      )}
    </nav>
  );
}

function NavItem({ label, icon, active, expandable, selected }) {
  // For Home, use a single class to avoid double border
  const classes = active && selected ? 'nav-item active' : 
                  active ? 'nav-item active' : 
                  selected ? 'nav-item selected' : 
                  'nav-item';
                  
  return (
    <div className={classes}>
      <span className="nav-icon">{icon}</span>
      <span className="nav-label">{label}</span>
      {expandable && <span className="nav-expand">›</span>}
    </div>
  );
}

// Format message text with markdown for AI messages
const formatMessageText = (text, isAI) => {
  if (!text) return '';
  
  if (isAI) {
    // For AI messages, use ReactMarkdown to render markdown syntax
    return <ReactMarkdown>{text}</ReactMarkdown>;
  }
  
  // For user messages, just use regular text formatting
  const paragraphs = text.split('\n\n');
  
  if (paragraphs.length > 1) {
    return paragraphs.map((paragraph, i) => (
      <p key={i}>{paragraph.replace(/\n/g, ' ')}</p>
    ));
  }
  
  // If single paragraph, just return the text
  return <p>{text}</p>;
};

function CalendarInterface({ userDetails }) {
  const [isTalkToKaleActive, setIsTalkToKaleActive] = useState(false);
  const [appointments, setAppointments] = useState({ Today: [], Upcoming: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentAppointment, setCurrentAppointment] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    start: '',
    end: ''
  });
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  
  // Subscribe to call status changes
  useEffect(() => {
    console.log('Setting up call status subscription');
    // Subscribe to call status changes from TalkToKale module
    const unsubscribe = subscribeToCallStatus((isActive) => {
      console.log('Call status changed to:', isActive);
      setIsTalkToKaleActive(isActive);
    });
    
    // Cleanup function to unsubscribe when component unmounts
    return () => {
      console.log('Unsubscribing from call status');
      unsubscribe();
    };
  }, []);
  
  // Fetch appointments when component mounts
  useEffect(() => {
    if (userDetails && userDetails.id) {
      fetchAppointments(userDetails.id);
    }
  }, [userDetails]);
  
  const fetchAppointments = async (userId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      // Append the user_id as a query parameter for a GET request
      const url = `https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/appointments?user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch appointments');
      }
      
      const data = await response.json();
      console.log('Appointments data:', data);
      
      // Ensure Upcoming is correctly spelled (API returns "Upcomming")
      const formattedData = {
        Today: data.Today || [],
        Upcoming: data.Upcomming || []
      };
      
      setAppointments(formattedData);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle talk to Kale button click
  const handleTalkToKaleClick = () => {
    if (isTalkToKaleActive) {
      stopCall();
    } else {
      startCall();
    }
  };

  // Helper function to format date and calculate days/hours until appointment
  const formatAppointmentInfo = (appointment) => {
    // Extract date from the start time string
    const startDate = new Date(appointment.start);
    const now = new Date();
    
    // Format start and end times
    const formatTime = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };
    
    // Calculate days until the appointment
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const appointmentDay = new Date(startDate);
    appointmentDay.setHours(0, 0, 0, 0);
    
    const diffTime = appointmentDay - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // If the appointment is today, calculate hours until start
    let hoursUntil = 0;
    if (diffDays === 0) {
      const diffMs = startDate - now;
      if (diffMs > 0) {
        hoursUntil = Math.ceil(diffMs / (1000 * 60 * 60));
      }
    }
    
    return {
      startTime: formatTime(appointment.start),
      endTime: formatTime(appointment.end),
      daysUntil: diffDays,
      hoursUntil: hoursUntil
    };
  };
  
  // Get user name from localStorage
  const getUserName = () => {
    try {
      return userDetails?.first_name || 'there';
    } catch (error) {
      return 'there';
    }
  };

  // Handle edit button click
  const handleEditClick = (appointment, e) => {
    e.stopPropagation(); // Prevent card click event
    setCurrentAppointment(appointment);
    
    // Ensure proper ISO string format for datetime inputs
    const startDate = new Date(appointment.start);
    const endDate = new Date(appointment.end);
    
    // Format with proper timezone handling
    const formatDateForInput = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    setEditFormData({
      title: appointment.title || '',
      description: appointment.description || '',
      start: formatDateForInput(startDate),
      end: formatDateForInput(endDate)
    });
    
    setEditModalOpen(true);
  };
  
  // Close modal
  const handleCloseModal = () => {
    setEditModalOpen(false);
    setCurrentAppointment(null);
  };
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };
  
  // Open modal for creating new appointment
  const handleCreateNew = () => {
    // Set default times: current time rounded to nearest 15 min, and 30 min later
    const now = new Date();
    now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15);
    now.setSeconds(0);
    now.setMilliseconds(0);
    
    const thirtyMinLater = new Date(now);
    thirtyMinLater.setMinutes(now.getMinutes() + 30);
    
    // Format dates for input fields
    const formatDateForInput = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    setCurrentAppointment(null);
    setEditFormData({
      title: '',
      description: '',
      start: formatDateForInput(now),
      end: formatDateForInput(thirtyMinLater)
    });
    setIsCreatingNew(true);
    setEditModalOpen(true);
    setError(null);
  };

  // Handle form submission
  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    
    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      // Validate that end date/time is after start date/time
      const startDateTime = new Date(editFormData.start);
      const endDateTime = new Date(editFormData.end);
      
      if (endDateTime <= startDateTime) {
        // Set end time to 30 minutes after start time
        const newEndTime = new Date(startDateTime);
        newEndTime.setMinutes(newEndTime.getMinutes() + 30);
        
        setEditFormData({
          ...editFormData,
          end: newEndTime.toISOString().slice(0, 16)
        });
        
        setError('End time must be after start time. Automatically adjusted.');
        return;
      }
      
      // Prepare appointment data
      const appointmentData = {
        id: isCreatingNew ? 0 : currentAppointment.id,
        title: editFormData.title,
        description: editFormData.description,
        start: new Date(editFormData.start).toISOString(),
        end: new Date(editFormData.end).toISOString(),
        user_id: userDetails.id
      };
      
      // Always use the same endpoint with POST
      const url = `https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/appointments`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        },
        body: JSON.stringify(appointmentData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${isCreatingNew ? 'create' : 'update'} appointment`);
      }
      
      const responseData = await response.json();
      console.log('API response:', responseData);
      
      // Update local appointments data
      if (responseData && responseData.Today && responseData.Upcomming) {
        setAppointments({
          Today: responseData.Today || [],
          Upcoming: responseData.Upcomming || []
        });
      } else {
        // If response doesn't include full appointment list, refresh from API
        if (userDetails && userDetails.id) {
          fetchAppointments(userDetails.id);
        }
      }
      
      // Clear any error
      setError(null);
      
      // Close modal
      setEditModalOpen(false);
      setCurrentAppointment(null);
      setIsCreatingNew(false);
      
    } catch (error) {
      console.error(`Error ${isCreatingNew ? 'creating' : 'updating'} appointment:`, error);
      setError(error.message);
    }
  };

  return (
    <div className={`calendar-interface ${isTalkToKaleActive ? 'call-active' : ''}`}>
      <div className="calendar-header">
        <h1>Welcome back, {getUserName()}!</h1>
        <div className="calendar-info">
          <div className="event-container">
            {appointments.Today.length === 1 ? (
              <p className="event-count">You have 1 event today</p>
            ) : (
              <p className="event-count">You have {appointments.Today.length} events today</p>
            )}
            <span className="current-date">{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</span>
          </div>
          <div className="calendar-actions">
            <button className="new-appointment-button" onClick={handleCreateNew}>
              <Plus size={16} weight="bold" />
              <span>New Appointment</span>
            </button>
          </div>
        </div>
      </div>
      
      {isLoading ? (
        <div className="loading-message">
          <div className="loading-indicator">Loading your appointments...</div>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="retry-button" onClick={() => fetchAppointments(userDetails.id)}>
            Retry
          </button>
        </div>
      ) : (
        <div className="calendar-content">
          <div className="calendar-left">
            {appointments.Today.length > 0 && (
              <div className="appointments-section">
                <h2 className="section-heading">Today</h2>
                {appointments.Today.map(appointment => {
                  const { startTime, endTime, hoursUntil } = formatAppointmentInfo(appointment);
                  
                  return (
                    <div 
                      key={appointment.id} 
                      className="appointment-card today"
                    >
                      <div className="appointment-time-indicator">
                        <div className="time-block today">
                          <span className="time-value">{hoursUntil > 0 ? hoursUntil : 'Now'}</span>
                          <span className="time-unit">{hoursUntil !== 1 ? 'hrs' : 'hr'}</span>
                        </div>
                        <span className="time-status">{hoursUntil > 0 ? 'until start' : 'starting'}</span>
                      </div>
                      
                      <div className="appointment-details">
                        <h3 className="appointment-title">{appointment.title}</h3>
                        <div className="appointment-time">
                          <span className="time-icon"></span>
                          <span>{startTime} - {endTime}</span>
                        </div>
                        <p className="appointment-description">{appointment.description}</p>
                      </div>
                      
                      <div className="appointment-actions">
                        <button 
                          className="edit-button"
                          onClick={(e) => handleEditClick(appointment, e)}
                          aria-label="Edit appointment"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            <h2 className="section-heading">Upcoming</h2>
            
            <div className="appointments-section">
              {appointments.Upcoming.length > 0 ? (
                appointments.Upcoming.map(appointment => {
                  const { startTime, endTime, daysUntil } = formatAppointmentInfo(appointment);
                  
                  return (
                    <div 
                      key={appointment.id} 
                      className="appointment-card"
                    >
                      <div className="appointment-time-indicator">
                        <div className="time-block">
                          <span className="time-value">{daysUntil}</span>
                          <span className="time-unit">{daysUntil === 1 ? 'day' : 'days'}</span>
                        </div>
                        <span className="time-status">until start</span>
                      </div>
                      
                      <div className="appointment-details">
                        <h3 className="appointment-title">{appointment.title}</h3>
                        <div className="appointment-time">
                          <span className="time-icon"></span>
                          <span>{startTime} - {endTime}</span>
                        </div>
                        <p className="appointment-description">{appointment.description}</p>
                      </div>
                      
                      <div className="appointment-actions">
                        <button 
                          className="edit-button"
                          onClick={(e) => handleEditClick(appointment, e)}
                          aria-label="Edit appointment"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-appointments-message">
                  <p>No upcoming appointments</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="calendar-right">
            <div className="vapi-assistant-container">
              <div className="vapi-assistant-header">
                <h3 className="vapi-assistant-title">Kale Assistant</h3>
              </div>
              
              <div className="vapi-content">
                {!isTalkToKaleActive ? (
                  <div className="vapi-placeholder">
                    <SpeakerHigh size={48} weight="thin" color="#2e7d32" />
                    <h3>Talk to Kale</h3>
                    <p>Your real estate AI assistant is ready to help</p>
                  </div>
                ) : (
                  <div className="call-active-message">
                    <button 
                      className="end-call-button"
                      onClick={handleTalkToKaleClick}
                    >
                      <PhoneX size={20} weight="fill" style={{marginRight: '8px'}} />
                      End Call
                    </button>
                  </div>
                )}
              </div>
              
              <div className="vapi-footer">
                <div className="talk-to-kale-floating">
                  <button 
                    className={`talk-to-kale-button ${isTalkToKaleActive ? 'active' : ''}`}
                    onClick={handleTalkToKaleClick}
                    aria-label={isTalkToKaleActive ? 'End call with Kale' : 'Talk to Kale'}
                  >
                    {isTalkToKaleActive ? (
                      <PhoneX size={20} weight="fill" />
                    ) : (
                      <SpeakerHigh size={20} weight="fill" />
                    )}
                    <span>{isTalkToKaleActive ? 'End Call' : 'Talk to Kale'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Appointment Modal */}
      {editModalOpen && (
        <div className="modal-overlay">
          <div className="edit-modal">
            <div className="modal-header">
              <h2>{isCreatingNew ? 'New Appointment' : 'Edit Appointment'}</h2>
              <button className="close-modal-button" onClick={handleCloseModal}>
                <X size={20} weight="bold" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitEdit}>
              <div className="form-group">
                <label htmlFor="title">Title</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={editFormData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={editFormData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <div className="date-field-container">
                    <input 
                      type="date" 
                      value={editFormData.start ? editFormData.start.split('T')[0] : ''}
                      onChange={(e) => {
                        const currentTime = editFormData.start ? editFormData.start.split('T')[1] : '00:00';
                        
                        setEditFormData({
                          ...editFormData, 
                          start: `${e.target.value}T${currentTime}`
                        });
                        
                        // Clear any error if present
                        if (error) {
                          setError(null);
                        }
                      }}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group time-select">
                  <label>Start Time</label>
                  <select 
                    className="time-dropdown"
                    value={
                      editFormData.start ? 
                        (new Date(editFormData.start).getHours().toString().padStart(2, '0') + 
                         ':' + 
                         new Date(editFormData.start).getMinutes().toString().padStart(2, '0')) : 
                        '09:00'
                    }
                    onChange={(e) => {
                      const date = editFormData.start ? editFormData.start.split('T')[0] : new Date().toISOString().split('T')[0];
                      const [hours, minutes] = e.target.value.split(':');
                      
                      setEditFormData({
                        ...editFormData, 
                        start: `${date}T${hours}:${minutes}:00`
                      });
                      
                      // Clear any error if present
                      if (error) {
                        setError(null);
                      }
                    }}
                    required
                  >
                    {Array.from({ length: 24 }, (_, hour) => {
                      return [0, 15, 30, 45].map(minute => {
                        const h = hour.toString().padStart(2, '0');
                        const m = minute.toString().padStart(2, '0');
                        const hourDisplay = hour === 0 ? '12' : 
                                           hour > 12 ? (hour - 12).toString() : 
                                           hour.toString();
                        const ampm = hour < 12 ? 'AM' : 'PM';
                        return (
                          <option key={`${h}:${m}`} value={`${h}:${m}`}>
                            {hourDisplay}:{m} {ampm}
                          </option>
                        );
                      });
                    }).flat()}
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>End Date</label>
                  <div className="date-field-container">
                    <input 
                      type="date" 
                      value={editFormData.end ? editFormData.end.split('T')[0] : ''}
                      min={editFormData.start ? editFormData.start.split('T')[0] : ''}
                      onChange={(e) => {
                        const currentTime = editFormData.end ? editFormData.end.split('T')[1] : '00:00';
                        const startDate = new Date(editFormData.start);
                        const newEndDate = new Date(`${e.target.value}T${currentTime}`);
                        
                        // If new end date is earlier than start date, set end to same day but keep time
                        if (newEndDate < startDate) {
                          // Set error message
                          setError('End date cannot be before start date. Adjusted to start date.');
                          
                          setEditFormData({
                            ...editFormData,
                            end: `${editFormData.start.split('T')[0]}T${currentTime}`
                          });
                        } else {
                          // Clear error if present
                          if (error && error.includes('date cannot be before')) {
                            setError(null);
                          }
                          
                          setEditFormData({
                            ...editFormData, 
                            end: `${e.target.value}T${currentTime}`
                          });
                        }
                      }}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group time-select">
                  <label>End Time</label>
                  <select 
                    className="time-dropdown"
                    value={
                      editFormData.end ? 
                        (new Date(editFormData.end).getHours().toString().padStart(2, '0') + 
                         ':' + 
                         new Date(editFormData.end).getMinutes().toString().padStart(2, '0')) : 
                        '09:30'
                    }
                    onChange={(e) => {
                      const endDate = editFormData.end ? editFormData.end.split('T')[0] : new Date().toISOString().split('T')[0];
                      const [hours, minutes] = e.target.value.split(':');
                      const newEndDateTime = new Date(`${endDate}T${hours}:${minutes}:00`);
                      const startDateTime = new Date(editFormData.start);
                      
                      // If end date is same as start date and end time is earlier than start time
                      if (endDate === editFormData.start.split('T')[0] && newEndDateTime < startDateTime) {
                        // Set error message
                        setError('End time cannot be before start time. Adjusted to start time.');
                        
                        // Set end time to at least start time
                        const startHours = startDateTime.getHours().toString().padStart(2, '0');
                        const startMinutes = startDateTime.getMinutes().toString().padStart(2, '0');
                        setEditFormData({
                          ...editFormData, 
                          end: `${endDate}T${startHours}:${startMinutes}:00`
                        });
                      } else {
                        // Clear error if present
                        if (error && error.includes('time cannot be before')) {
                          setError(null);
                        }
                        
                        setEditFormData({
                          ...editFormData, 
                          end: `${endDate}T${hours}:${minutes}:00`
                        });
                      }
                    }}
                    required
                  >
                    {Array.from({ length: 24 }, (_, hour) => {
                      return [0, 15, 30, 45].map(minute => {
                        const h = hour.toString().padStart(2, '0');
                        const m = minute.toString().padStart(2, '0');
                        const hourDisplay = hour === 0 ? '12' : 
                                           hour > 12 ? (hour - 12).toString() : 
                                           hour.toString();
                        const ampm = hour < 12 ? 'AM' : 'PM';
                        return (
                          <option key={`${h}:${m}`} value={`${h}:${m}`}>
                            {hourDisplay}:{m} {ampm}
                          </option>
                        );
                      });
                    }).flat()}
                  </select>
                </div>
              </div>
              
              {error && <div className="error-message">{error}</div>}
              
              <div className="modal-actions">
                <button type="button" className="cancel-button" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Upload Page Component
function UploadPage({ userDetails, onNavClick }) {
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [importedData, setImportedData] = useState(null);

  // Log user details on component mount
  useEffect(() => {
    if (userDetails) {
      console.log('User ID:', userDetails.id);
      console.log('Teams ID:', userDetails.teams_id);
    }
  }, [userDetails]);

  useEffect(() => {
    if (userDetails && userDetails.teams_id) {
      fetchTeamMembers();
    }
  }, [userDetails]);

  const fetchTeamMembers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Specifically use teams_id from user details
      const teamsId = userDetails?.teams_id;
      const authToken = localStorage.getItem('authToken');
      
      if (!teamsId) {
        console.error('No teams_id found in user details:', userDetails);
        setError('Missing team information. Please contact support.');
        setLoading(false);
        return;
      }
      
      if (!authToken) {
        console.error('No auth token found');
        setError('Authentication error. Please log in again.');
        setLoading(false);
        return;
      }
      
      console.log('Fetching team members with teams_id:', teamsId);
      
      // Base URL for the API
      const url = 'https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/users';
      
      // Add teams_id as the team_id parameter
      const fullUrl = `${url}?team_id=${teamsId}`;
      
      console.log('Fetching team members from URL:', fullUrl);
      
      // Make the GET request
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch team members');
      }
      
      const data = await response.json();
      console.log('Fetched team members:', data);
      
      setTeamMembers(data);
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError('Failed to load team members. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    
    // Determine file type - only accept CSV files
    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    if (fileExtension === 'csv') {
      setFileType('csv');
      // Reset imported data when selecting a new file
      setImportedData(null);
    } else {
      setFileType(null);
      setFile(null);
      setUploadStatus({
        success: false,
        message: 'Please upload a CSV file only.'
      });
    }
  };

  // Helper function to extract profile image URL from different possible data structures
  const getProfileImageUrl = (profileData) => {
    if (!profileData) return null;
    
    // Check direct URL
    if (typeof profileData === 'string') return profileData;
    
    // Check common formats
    if (profileData.url) return profileData.url;
    if (profileData.picture?.url) return profileData.picture.url;
    if (profileData.image?.url) return profileData.image.url;
    if (profileData.profile_pic?.url) return profileData.profile_pic.url;
    
    // For nested structures
    for (const key in profileData) {
      if (profileData[key] && typeof profileData[key] === 'object') {
        if (profileData[key].url) return profileData[key].url;
      }
    }
    
    return null;
  };
  
  const handleTeamMemberSelect = (memberId) => {
    setSelectedTeamMember(memberId === selectedTeamMember ? null : memberId);
    // Reset imported data when selecting a different team member
    setImportedData(null);
  };

  const handleUpload = async () => {
    if (!file || !selectedTeamMember) {
      setUploadStatus({
        success: false,
        message: 'Please select a CSV file and a team member before uploading.'
      });
      return;
    }

    setUploading(true);
    setUploadStatus(null);
    setImportedData(null);

    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        throw new Error('Authentication error. Please log in again.');
      }
      
      // Create FormData object for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);
      formData.append('user_id', selectedTeamMember);
      
      // Include the team_id parameter from userDetails
      if (userDetails && userDetails.teams_id) {
        formData.append('team_id', userDetails.teams_id);
      } else {
        throw new Error('Missing team ID. Please contact support.');
      }
      
      // Upload to the specified API endpoint
      const response = await fetch('https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/csv_import', {
        method: 'POST',
        headers: {
          'Authorization': authToken
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload CSV file');
      }
      
      const data = await response.json();
      console.log('CSV import response:', data);
      
      setImportedData(data);
      
      setUploadStatus({
        success: true,
        message: `CSV file successfully uploaded and data imported.`
      });
      
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadStatus({
        success: false,
        message: 'Error uploading file: ' + error.message
      });
    } finally {
      setUploading(false);
    }
  };

  const handleApproveData = async () => {
    if (!importedData || importedData.length === 0) {
      return;
    }
    
    setUploading(true);
    setUploadStatus({
      success: true,
      message: 'Saving data...'
    });
    
    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        throw new Error('Authentication error. Please log in again.');
      }
      
      // Send API request to save the data
      const response = await fetch('https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/pdf_import/save', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        },
        body: JSON.stringify({
          status: 'save',
          leads: importedData
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save data');
      }
      
      // On successful save, navigate to the "get" page
      setUploadStatus({
        success: true,
        message: 'Data has been saved successfully!'
      });
      
      // Reset form
      setFile(null);
      setFileType(null);
      setSelectedTeamMember(null);
      setImportedData(null);
      
      // Safely reset file input if it exists
      const fileInput = document.getElementById('file-upload');
      if (fileInput) {
        fileInput.value = '';
      }
      
      // Navigate to the "get" page immediately
      if (typeof onNavClick === 'function') {
        // Give a brief moment for the success message to be seen
        setTimeout(() => {
          onNavClick('leads_get');
        }, 500);
      }
      
    } catch (error) {
      console.error('Error saving data:', error);
      setUploadStatus({
        success: false,
        message: 'Error saving data: ' + error.message
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteData = async () => {
    if (!importedData || importedData.length === 0) {
      return;
    }
    
    setUploading(true);
    setUploadStatus({
      success: true,
      message: 'Discarding data...'
    });
    
    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        throw new Error('Authentication error. Please log in again.');
      }
      
      // Send API request to discard the data
      const response = await fetch('https://xin7-2on4-t5ue.n7.xano.io/api:bIID2SK9/pdf_import/save', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken
        },
        body: JSON.stringify({
          status: 'delete',
          leads: importedData
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to discard data');
      }
      
      // On successful discard
      setUploadStatus({
        success: true,
        message: 'Imported data has been discarded.'
      });
      
      // Reset the state to show the upload form again
      setFile(null);
      setFileType(null);
      setSelectedTeamMember(null);
      setImportedData(null);
      
      // Safely reset file input if it exists
      const fileInput = document.getElementById('file-upload');
      if (fileInput) {
        fileInput.value = '';
      }
      
    } catch (error) {
      console.error('Error discarding data:', error);
      setUploadStatus({
        success: false,
        message: 'Error discarding data: ' + error.message
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="upload-page">
      <div className="upload-container">
        <h2>Upload Canvassing List</h2>
        <p className="upload-description">This is where you upload lead lists for canvassing. Import potential client data from CSV files to quickly build your pipeline and automate follow-ups.</p>
        
        {!importedData ? (
          <div className="upload-form">
            <div className="form-group">
              <label htmlFor="file-upload">Select Lead Source Document</label>
              <div className="file-input-container">
                <input 
                  type="file" 
                  id="file-upload" 
                  accept=".csv" 
                  onChange={handleFileChange}
                  disabled={uploading}
                  className="file-input"
                />
                <div className="file-input-button">
                  Choose CSV File
                </div>
              </div>
              {file && (
                <div className="selected-file">
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">({(file.size / 1024).toFixed(1)} KB)</span>
                </div>
              )}
            </div>
            
            <div className="form-group mt-20">
              <label>Select Team Member to Assign</label>
              
              {loading ? (
                <div className="loading-indicator">Loading team members...</div>
              ) : error ? (
                <div className="error-message">{error}</div>
              ) : teamMembers.length === 0 ? (
                <div className="no-members">No team members found</div>
              ) : (
                <div className="member-selection-grid">
                  {teamMembers.map(member => (
                    <div 
                      key={member.id} 
                      className={`member-selection-card ${selectedTeamMember === member.id ? 'selected' : ''}`}
                      onClick={() => handleTeamMemberSelect(member.id)}
                    >
                      <div className="member-select-avatar">
                        {console.log('Profile picture data:', member.profile_picture)}
                        {member.profile_picture ? (
                          <img 
                            src={getProfileImageUrl(member.profile_picture)} 
                            alt={member.first_name} 
                          />
                        ) : (
                          <div className="member-initial">{member.first_name?.charAt(0) || '?'}</div>
                        )}
                      </div>
                      <div className="member-select-info">
                        <h3 className="member-name">{member.first_name}</h3>
                        <p className="member-role">{member.role_title || 'Team Member'}</p>
                      </div>
                      {selectedTeamMember === member.id && (
                        <div className="member-selected-indicator"></div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <button 
              className="upload-button" 
              onClick={handleUpload}
              disabled={!file || !selectedTeamMember || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Lead Source'}
            </button>
            
            {uploadStatus && !importedData && (
              <div className={`upload-status ${uploadStatus.success ? 'success' : 'error'}`}>
                {uploadStatus.message}
              </div>
            )}
          </div>
        ) : (
          <div className="imported-data-container">
            <h3>Lead Data Preview</h3>
            <p className="imported-count">Found {importedData.length} potential clients from the document</p>
            
            <div className="imported-data-list">
              {importedData.map(item => (
                <div key={item.id} className="imported-data-item">
                  <div className="imported-data-header">
                    <h3>{item.name || 'Unnamed Contact'}</h3>
                    <span className="imported-data-type">{item.type === 'csv_inspection' ? 'pending save' : item.type || 'contact'}</span>
                  </div>
                  <div className="imported-data-details">
                    {item.seller_property_address && (
                      <div className="imported-data-field">
                        <span className="field-label">Address:</span>
                        <span className="field-value">{item.seller_property_address}</span>
                      </div>
                    )}
                    {item.contact_number && (
                      <div className="imported-data-field">
                        <span className="field-label">Phone:</span>
                        <span className="field-value">{item.contact_number}</span>
                      </div>
                    )}
                    {item.email && (
                      <div className="imported-data-field">
                        <span className="field-label">Email:</span>
                        <span className="field-value">{item.email}</span>
                      </div>
                    )}
                    {item.id_number && (
                      <div className="imported-data-field">
                        <span className="field-label">ID Number:</span>
                        <span className="field-value">{item.id_number}</span>
                      </div>
                    )}
                    {item.age && (
                      <div className="imported-data-field">
                        <span className="field-label">Age:</span>
                        <span className="field-value">{item.age}</span>
                      </div>
                    )}
                    {item.gender && (
                      <div className="imported-data-field">
                        <span className="field-label">Gender:</span>
                        <span className="field-value">{item.gender}</span>
                      </div>
                    )}
                    {item.sellers_property_value && (
                      <div className="imported-data-field">
                        <span className="field-label">Property Value:</span>
                        <span className="field-value">{item.sellers_property_value}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="imported-data-actions">
              <button 
                className="approve-button" 
                onClick={handleApproveData}
                disabled={uploading}
              >
                {uploading && uploadStatus?.message === 'Saving data...' ? 'Saving...' : 'Import Leads'}
              </button>
              <button 
                className="delete-button" 
                onClick={handleDeleteData}
                disabled={uploading}
              >
                {uploading && uploadStatus?.message === 'Discarding data...' ? 'Discarding...' : 'Discard Leads'}
              </button>
            </div>
            
            {uploadStatus && (
              <div className={`upload-status ${uploadStatus.success ? 'success' : 'error'}`}>
                {uploadStatus.message}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Settings Page Component
function SettingsPage({ userDetails, onLogout }) {
  return (
    <div className="settings-page">
      <div className="settings-container">
        <h1>Settings</h1>
        
        <div className="settings-section">
          <h2>Account Information</h2>
          
          <div className="account-info">
            <div className="profile-section">
              {userDetails.profile_pic ? (
                <img 
                  src={userDetails.profile_pic.url} 
                  alt={`${userDetails.first_name} ${userDetails.last_name}`} 
                  className="settings-profile-image" 
                />
              ) : (
                <div className="settings-profile-initial">
                  {userDetails.first_name?.charAt(0) || 'U'}
                </div>
              )}
              
              <div className="profile-details">
                <h3>{userDetails.first_name} {userDetails.last_name}</h3>
                <p className="profile-role">{userDetails.role_title}</p>
                <p className="profile-role-level"><strong>Role Level:</strong> {userDetails.role || 'Not set'}</p>
                <p className="profile-email">{userDetails.email}</p>
                <p className="profile-phone">{userDetails.cellphone_number}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="settings-section">
          <h2>Session</h2>
          
          <div className="session-actions">
            <button className="logout-button" onClick={onLogout}>
              Logout
            </button>
            <p className="session-info">
              Last login: {userDetails.last_login ? new Date(userDetails.last_login).toLocaleString() : 'Unknown'}
            </p>
          </div>
        </div>
        
        <div className="settings-section">
          <h2>Organization</h2>
          
          <div className="organization-info">
            <p><strong>Organization ID:</strong> {userDetails.organisations_id}</p>
            <p><strong>Organization Status:</strong> {userDetails.organisation_status}</p>
            <p><strong>Team ID:</strong> {userDetails.teams_id}</p>
            <p><strong>Role Level:</strong> {userDetails.role || 'Not set'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
